import { Link as RouterLink } from 'react-router-dom';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  RELATIVE_TIME_TEXT_SX,
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import {
  resolveSuiteEntityDisplayName,
  resolveSuiteEntitySecondaryLabel,
} from '@/features/suites/utils/suiteDisplayName';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getStatusCopy } from '@/shared/copy/status';
import {
  Box,
  formatDuration,
  formatFrequency,
  formatPercent,
  getStatusTextColor,
  Link,
  resolveStatusChipTone,
  Text,
  Tooltip,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { getStatusIconComponent, getThresholdChipKey, TableNameCell } from '@/ui/composites/table';
import {
  mapSuiteRunSummariesWithRunNumbers,
  mapTestRunSummaries,
} from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { MetricThresholdGroup, SuiteRunStatus, SuiteListItemDto } from '@flowqa/shared';

type SuitesColumnsOptions = {
  includeLatestTestsColumn?: boolean;
  includeLastExecutedColumn?: boolean;
  latestAsMeta?: boolean;
  includeTagsInNameColumn?: boolean;
  includeActionsColumn?: boolean;
  hideActionsColumn?: boolean;
  onEditRow?: (row: SuiteListItemDto) => void;
  onDeleteRow?: (row: SuiteListItemDto) => void;
  deleteRowPendingId?: string | null;
  tagColorByName?: Record<string, TagColor>;
  onUpdateRowTagsColor?: (input: {
    row: SuiteListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId?: string | null;
  metricSeriesBySuiteId?: Map<
    string,
    {
      dates: Date[];
      passRate: Array<number | null>;
      frequencyPerDay: Array<number | null>;
      flakeScore: Array<number | null>;
      averageDurationMs: Array<number | null>;
    }
  >;
  selectedProjectId: string | null;
  groupErroredWithFailed?: boolean;
  preferFileNameOverDisplayName?: boolean;
  resolveGroupErroredWithFailed?: (projectId: string) => boolean;
  resolvePreferFileNameOverDisplayName?: (projectId: string) => boolean;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: SuiteRunStatus | null | undefined) => void;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  showDeltas?: boolean;
  disableLinks?: boolean;
  disableSummaryLinks?: boolean;
};

const columnWidths = tableColumnWidths.suites;

export const buildSuitesColumns = ({
  includeLatestTestsColumn = true,
  includeLastExecutedColumn = true,
  latestAsMeta = false,
  includeTagsInNameColumn = false,
  includeActionsColumn = false,
  hideActionsColumn = false,
  onEditRow,
  onDeleteRow,
  deleteRowPendingId = null,
  tagColorByName = {},
  metricSeriesBySuiteId: _metricSeriesBySuiteId,
  selectedProjectId,
  groupErroredWithFailed = false,
  preferFileNameOverDisplayName = false,
  resolveGroupErroredWithFailed,
  resolvePreferFileNameOverDisplayName,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showDeltas = true,
  disableLinks = false,
  disableSummaryLinks = false,
}: SuitesColumnsOptions): Array<GridColDef<SuiteListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: SuiteListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const resolveTagTone = (tag: string): TagColor => {
    const trimmed = tag.trim();
    const fallback = trimmed.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(tagColorByName, trimmed)) {
      return tagColorByName[trimmed];
    }
    if (Object.prototype.hasOwnProperty.call(tagColorByName, fallback)) {
      return tagColorByName[fallback];
    }
    return 'neutral';
  };
  const nameColumn: GridColDef<SuiteListItemDto> = {
    field: 'name',
    headerName: 'Suite',
    flex: columnWidths.name.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'table-name-cell' : undefined,
    filterOperators: stringOperators,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      const rowPreferFileNameOverDisplayName =
        resolvePreferFileNameOverDisplayName?.(params.row.projectId) ??
        preferFileNameOverDisplayName;
      const primaryLabel = resolveSuiteEntityDisplayName(params.row, {
        preferFileNameOverDisplayName: rowPreferFileNameOverDisplayName,
      });
      const secondaryLabel = resolveSuiteEntitySecondaryLabel(params.row, {
        preferFileNameOverDisplayName: rowPreferFileNameOverDisplayName,
      });
      if (latestAsMeta) {
        const tags = resolveEntityTags(params.row);
        const suiteHref = disableLinks ? null : `/suites/${params.row.slug}`;
        const resolvedTags = includeTagsInNameColumn
          ? tags.map((tag) => ({ label: tag, tone: resolveTagTone(tag) }))
          : [];
        return (
          <TableCellContainer gap="none">
            <TableNameCell
              primary={{
                label: primaryLabel,
                ...(suiteHref
                  ? {
                      href: suiteHref,
                      onClick: () => {
                        onProjectSelect(params.row.projectId);
                      },
                    }
                  : {}),
              }}
              secondary={
                secondaryLabel
                  ? {
                      label: secondaryLabel,
                      ...(suiteHref
                        ? {
                            href: suiteHref,
                            onClick: () => {
                              onProjectSelect(params.row.projectId);
                            },
                          }
                        : {}),
                    }
                  : null
              }
              tags={resolvedTags}
            />
          </TableCellContainer>
        );
      }
      return (
        <TableNameCell
          primary={{
            label: primaryLabel,
            ...(disableLinks
              ? {}
              : {
                  href: `/suites/${params.row.slug}`,
                  onClick: () => {
                    onProjectSelect(params.row.projectId);
                  },
                }),
          }}
          secondary={
            secondaryLabel
              ? {
                  label: secondaryLabel,
                  ...(disableLinks
                    ? {}
                    : {
                        href: `/suites/${params.row.slug}`,
                        onClick: () => {
                          onProjectSelect(params.row.projectId);
                        },
                      }),
                }
              : null
          }
        />
      );
    },
  };

  const latestSummaryColumn: GridColDef<SuiteListItemDto> = {
    field: 'latestRunStatus',
    headerName: getChartTitle('latestStatus'),
    flex: columnWidths.latestStatus.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'table-latest-meta-cell' : undefined,
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      const isLoadingRow = (params.row as { __loading?: boolean }).__loading === true;
      if (isLoadingRow) {
        return null;
      }
      const latestRun = params.row.latestRun;
      const rawStatus = latestRun?.status ?? null;
      const rowGroupErroredWithFailed =
        resolveGroupErroredWithFailed?.(params.row.projectId) ?? groupErroredWithFailed;
      const status = resolveProjectDisplayStatus(rawStatus, rowGroupErroredWithFailed);
      if (latestAsMeta) {
        const statusLabel =
          resolveProjectDisplayStatusLabel(rawStatus, rowGroupErroredWithFailed) || '-';
        const statusTone = resolveStatusChipTone(status);
        const durationLabel =
          latestRun?.durationMs !== null && latestRun?.durationMs !== undefined
            ? formatDuration(latestRun.durationMs)
            : '';
        const timestamp = latestRun?.timestamp ?? null;
        const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
        const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
        const resolvedTimestampTitle =
          timestampTitle || (timestamp instanceof Date ? timestamp.toISOString() : timestamp);
        const runNumber = latestRun?.runNumber ?? null;
        const executionHref =
          runNumber !== null && params.row.slug && !disableLinks
            ? `/suites/${params.row.slug}/runs/${runNumber}`
            : null;
        return (
          <TableCellContainer gap="none">
            <TableStatusMetaContent
              statusLabel={statusLabel}
              statusHref={executionHref}
              onStatusClick={(event) => {
                event.stopPropagation();
                onProjectSelect(params.row.projectId);
              }}
              chipTone={statusTone}
              chipIcon={<Box component={getStatusIconComponent(status)} />}
              chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
              chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
              durationLabel={durationLabel}
              durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
              timestampLabel={timestampLabel}
              timestampTitle={resolvedTimestampTitle}
              timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            />
          </TableCellContainer>
        );
      }
      const isClickable = Boolean(status && onStatusSelect);
      const label = rawStatus
        ? resolveProjectDisplayStatusLabel(rawStatus, rowGroupErroredWithFailed)
        : 'Untested';
      const runNumber = latestRun?.runNumber ?? null;
      const executionHref =
        runNumber !== null && params.row.slug
          ? `/suites/${params.row.slug}/runs/${runNumber}`
          : null;
      const labelNode =
        executionHref && !disableLinks ? (
          <Link
            component={RouterLink}
            to={executionHref}
            underline="hover"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(params.row.projectId);
            }}
          >
            <Text role="body" component="span" color="inherit">
              {label}
            </Text>
          </Link>
        ) : (
          label
        );
      if (isClickable && !executionHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? null);
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };
  const lastExecutedColumn: GridColDef<SuiteListItemDto, Date | string | null> = {
    field: 'latestRunTimestamp',
    headerName: 'Last Executed',
    flex: columnWidths.lastExecuted.flex,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    valueGetter: (_value, row) => row.latestRun?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto, Date | string | null>) => {
      const timestamp = params.value ?? null;
      const label = timestamp ? formatRelativeTime(timestamp) : '-';
      const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      return (
        <Tooltip
          title={fullTimestamp}
          disableHoverListener={!fullTimestamp}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '15px',
                lineHeight: 1.4,
              },
            },
          }}
        >
          <Box component="span" sx={{ display: 'block', width: '100%' }}>
            <Text role="body" component="span" sx={RELATIVE_TIME_TEXT_SX}>
              {label.length > 0 ? label : '-'}
            </Text>
          </Box>
        </Tooltip>
      );
    },
  };

  const testsColumn: GridColDef<SuiteListItemDto> = {
    field: 'testLatestRunBreakdown',
    headerName: 'Latest Tests',
    flex: latestAsMeta ? columnWidths.latestTests.flex : 0.9,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestTestResults?.counts;
      const rowGroupErroredWithFailed =
        resolveGroupErroredWithFailed?.(params.row.projectId) ?? groupErroredWithFailed;
      const passed = counts?.passed ?? 0;
      const failed = counts?.failed ?? 0;
      const errored = counts?.errored ?? 0;
      const skipped = counts?.skipped ?? 0;
      const untested = counts?.untested ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="test"
            counts={{ passed, failed, errored, skipped, untested }}
            groupErroredWithFailed={rowGroupErroredWithFailed}
            summaryItems={mapTestRunSummaries(params.row.latestTestResults?.summaries, 'display')}
            disableSummaryLinks={disableSummaryLinks}
          />
        </TableCellContainer>
      );
    },
  };

  const executionsColumn: GridColDef<SuiteListItemDto> = {
    field: 'executionStatusCounts',
    headerName: 'Runs',
    flex: columnWidths.executions.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      const counts = params.row.executionStatusCounts;
      const rowGroupErroredWithFailed =
        resolveGroupErroredWithFailed?.(params.row.projectId) ?? groupErroredWithFailed;

      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="execution"
            totalIconEntity="suite"
            counts={{
              passed: counts?.passed ?? 0,
              failed: counts?.failed ?? 0,
              errored: counts?.errored ?? 0,
              skipped: counts?.skipped ?? 0,
              untested: counts?.untested ?? 0,
            }}
            groupErroredWithFailed={rowGroupErroredWithFailed}
            summaryItems={mapSuiteRunSummariesWithRunNumbers(params.row.executionSummaries)}
            summaryUnitLabel="suite"
            totalLinkMode="execution"
            disableSummaryLinks={disableSummaryLinks}
          />
        </TableCellContainer>
      );
    },
  };

  const newMetricsColumn: GridColDef<SuiteListItemDto> = {
    field: 'metricsSummary',
    headerName: getDetailLabel('metrics'),
    flex: columnWidths.metrics.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const passRate = params.row.metrics?.passRate ?? null;
      const passRateDelta = deltasEnabled ? (params.row.metrics?.delta?.passRate ?? null) : null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const frequencyDelta = deltasEnabled
        ? (params.row.metrics?.delta?.frequencyPerDay ?? null)
        : null;
      const flakeScore = params.row.metrics?.flakeScore ?? null;
      const flakeScoreDelta = deltasEnabled
        ? (params.row.metrics?.delta?.flakeScore ?? null)
        : null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationDelta = deltasEnabled
        ? (params.row.metrics?.delta?.averageDurationMs ?? null)
        : null;
      const resolvedPassRate = passRate;
      const resolvedPassRateDelta = resolvedPassRate === null ? null : passRateDelta;
      const resolvedFrequency = frequency;
      const resolvedFrequencyDelta = resolvedFrequency === null ? null : frequencyDelta;
      const resolvedFlakeScore = flakeScore;
      const resolvedFlakeScoreDelta = resolvedFlakeScore === null ? null : flakeScoreDelta;
      const resolvedAvgDuration = avgDuration;
      const resolvedAvgDurationDelta = resolvedAvgDuration === null ? null : avgDurationDelta;
      const resolvedAvgDurationThresholdValue =
        resolvedAvgDuration === null ? null : resolvedAvgDuration / 1000;
      const thresholdGroup = resolvedThresholds(params.row);
      const passRateTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedPassRate, thresholdGroup.passRate),
      );
      const frequencyTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedFrequency, thresholdGroup.frequency),
      );
      const flakeScoreTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedFlakeScore, thresholdGroup.flakeScore),
      );
      const avgDurationTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedAvgDurationThresholdValue, thresholdGroup.avgDuration),
      );

      const passRateLabel =
        resolvedPassRate === null ? getStatusCopy('noData') : `${formatPercent(resolvedPassRate)}%`;
      const passRateDeltaLabel =
        resolvedPassRateDelta === null
          ? null
          : resolvedPassRateDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedPassRateDelta > 0 ? '+' : ''}${formatPercent(resolvedPassRateDelta)}%`;
      const frequencyLabel =
        resolvedFrequency === null ? formatFrequency(0) : formatFrequency(resolvedFrequency);
      const frequencyDeltaLabel =
        resolvedFrequencyDelta === null
          ? null
          : resolvedFrequencyDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedFrequencyDelta > 0 ? '+' : resolvedFrequencyDelta < 0 ? '-' : ''}${formatFrequency(
                Math.abs(resolvedFrequencyDelta),
              )}`;
      const flakeScoreLabel =
        resolvedFlakeScore === null
          ? getStatusCopy('noData')
          : `${formatPercent(resolvedFlakeScore)}%`;
      const flakeScoreDeltaLabel =
        resolvedFlakeScoreDelta === null
          ? null
          : resolvedFlakeScoreDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedFlakeScoreDelta > 0 ? '+' : ''}${formatPercent(resolvedFlakeScoreDelta)}%`;
      const avgDurationLabel =
        resolvedAvgDuration === null
          ? getStatusCopy('noData')
          : formatDuration(resolvedAvgDuration);
      const avgDurationDeltaLabel =
        resolvedAvgDurationDelta === null
          ? null
          : resolvedAvgDurationDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedAvgDurationDelta > 0 ? '+' : '-'}${formatDuration(
                Math.abs(resolvedAvgDurationDelta),
                { roundMillisecondsWithSeconds: true },
              )}`;

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: getChartTitle('passRate'),
                valueLabel: passRateLabel,
                deltaValue: resolvedPassRateDelta,
                deltaLabel: passRateDeltaLabel,
                isMissing: resolvedPassRate === null,
                tone: passRateTone,
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: getChartTitle('frequency'),
                valueLabel: frequencyLabel,
                deltaValue: resolvedFrequencyDelta,
                deltaLabel: frequencyDeltaLabel,
                isMissing: resolvedFrequency === null,
                tone: frequencyTone,
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: getChartTitle('avgDuration'),
                valueLabel: avgDurationLabel,
                deltaValue: resolvedAvgDurationDelta,
                deltaLabel: avgDurationDeltaLabel,
                isMissing: resolvedAvgDuration === null,
                tone: avgDurationTone,
                direction: thresholdGroup.avgDuration.direction,
              },
              {
                key: 'flakeScore',
                label: getChartTitle('flakeScore'),
                valueLabel: flakeScoreLabel,
                deltaValue: resolvedFlakeScoreDelta,
                deltaLabel: flakeScoreDeltaLabel,
                isMissing: resolvedFlakeScore === null,
                tone: flakeScoreTone,
                direction: thresholdGroup.flakeScore.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<SuiteListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: columnWidths.actions.flex,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => (
      <TableActionsCell
        rowName={params.row.name}
        editActionLabel={getEntityActionLabel('edit', 'Suite')}
        deleteActionLabel={getEntityActionLabel('delete', 'Suite')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableEdit={deleteRowPendingId === params.row.id}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [
    nameColumn,
    latestSummaryColumn,
    ...(includeLatestTestsColumn ? [testsColumn] : []),
    ...(latestAsMeta ? [executionsColumn] : []),
    ...(latestAsMeta ? [newMetricsColumn] : []),
    ...(includeLastExecutedColumn ? [lastExecutedColumn] : []),
    ...(includeActionsColumn && !hideActionsColumn ? [actionsColumn] : []),
  ];
};
